import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";

interface AuditRow {
  id: string;
  event_type: string;
  created_at: string;
  event_data: Record<string, unknown> | null;
}

function shortHash(id: string) {
  return id.slice(0, 8);
}

export function AuditTransparencyTab() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void trackEvent("audit_transparency_viewed", { session_id: getSessionId() });
    let alive = true;
    const load = async () => {
      const since7d = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const { data } = await supabase
        .from("audit_master")
        .select("id,event_type,created_at,event_data")
        .in("event_type", [
          "smart_bonus_triggered",
          "smart_bonus_viewed",
          "smart_bonus_clicked",
          "smart_bonus_expired",
          "geo_banner_clicked",
          "halftime_offer_clicked",
        ])
        .gte("created_at", since7d)
        .order("created_at", { ascending: false })
        .limit(50);
      if (!alive) return;
      setRows((data as AuditRow[]) ?? []);
      setLoading(false);
    };
    void load();
    const iv = setInterval(load, 10000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  return (
    <div className="rounded-2xl glass p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />
        <div>
          <div className="text-sm font-black tracking-tight">Transparencia</div>
          <div className="text-[10px] font-bold tracking-widest text-muted-foreground">
            BETTING · AUDITORÍA EN VIVO
          </div>
        </div>
      </div>
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Cada interacción con bonos, ofertas y banners queda registrada en el servidor para
        auditoría. Esta vista demuestra trazabilidad de extremo a extremo.
      </p>

      <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto">
        {loading && <div className="text-[11px] text-muted-foreground">Cargando…</div>}
        {!loading && rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-3 text-center text-[11px] text-muted-foreground">
            Sin eventos auditados todavía. Interactúa con un bono u oferta para verlos aquí.
          </div>
        )}
        {rows.map((r) => {
          const data = (r.event_data ?? {}) as Record<string, unknown>;
          const odds = (data.simulated_odds as string | undefined) ?? "—";
          return (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/40 px-2.5 py-1.5 text-[11px]"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">{r.event_type}</div>
                <div className="truncate text-[10px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleTimeString()} · cuota sim {odds} · #
                  {shortHash(r.id)}
                </div>
              </div>
              <span className="rounded-full border border-[oklch(0.82_0.24_145)]/40 bg-[oklch(0.82_0.24_145)]/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-[oklch(0.82_0.24_145)]">
                REGISTRADO
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}